import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAddApplePayPlatformDomain } from '@/lib/api/apple-pay-platform';

type Environment = 'production' | 'sandbox';

/**
 * Add a merchant domain.
 *
 * Only the domain is genuinely required — the account and store are resolved
 * from the storefront routing table, and the Apple merchant identifier defaults
 * to the platform integrator ID. Those overrides exist for domains that aren't
 * routed yet, so they live behind a disclosure rather than presenting six empty
 * boxes and implying they all need answers.
 */
export function AddDomainDialog({ environment, disabled }: { environment: Environment; disabled?: boolean }) {
  const { t } = useTranslation('manageApplePay');
  const addDomain = useAddApplePayPlatformDomain();

  const [open, setOpen] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [domain, setDomain] = useState('');
  const [accountId, setAccountId] = useState('');
  const [storeId, setStoreId] = useState('');
  const [merchantIdentifier, setMerchantIdentifier] = useState('');
  const [merchantName, setMerchantName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [btRegistrationId, setBtRegistrationId] = useState('');

  const reset = () => {
    setDomain('');
    setAccountId('');
    setStoreId('');
    setMerchantIdentifier('');
    setMerchantName('');
    setDisplayName('');
    setBtRegistrationId('');
    setShowAdvanced(false);
  };

  const submit = () => {
    addDomain.mutate(
      {
        environment,
        domain: domain.trim(),
        ...(accountId.trim() ? { accountId: accountId.trim() } : {}),
        ...(storeId.trim() ? { storeId: storeId.trim() } : {}),
        ...(merchantIdentifier.trim()
          ? { partnerInternalMerchantIdentifier: merchantIdentifier.trim() }
          : {}),
        ...(merchantName.trim() ? { partnerMerchantName: merchantName.trim() } : {}),
        ...(displayName.trim() ? { displayName: displayName.trim() } : {}),
        ...(btRegistrationId.trim() ? { btMerchantRegistrationId: btRegistrationId.trim() } : {}),
      },
      {
        onSuccess: () => {
          reset();
          setOpen(false);
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm" disabled={disabled}>
          <Plus className="mr-1.5 h-4 w-4" />
          {t('domains.add')}
        </Button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('domains.addTitle')}</DialogTitle>
          <DialogDescription>{t('domains.addDescription')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="domain">{t('domains.domain')}</Label>
            <Input
              id="domain"
              value={domain}
              autoFocus
              onChange={(e) => setDomain(e.target.value)}
              placeholder={t('domains.domainPlaceholder')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && domain.trim() && !addDomain.isPending) submit();
              }}
            />
            <p className="text-muted-foreground text-xs">{t('domains.domainHint')}</p>
          </div>

          <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="px-0">
                <ChevronDown
                  className={`mr-1.5 h-4 w-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
                />
                {t('domains.advanced')}
              </Button>
            </CollapsibleTrigger>

            <CollapsibleContent className="space-y-4 pt-3">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="account-id">{t('domains.accountId')}</Label>
                  <Input id="account-id" value={accountId} onChange={(e) => setAccountId(e.target.value)} />
                  <p className="text-muted-foreground text-xs">{t('domains.accountIdHint')}</p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="store-id">{t('domains.storeId')}</Label>
                  <Input id="store-id" value={storeId} onChange={(e) => setStoreId(e.target.value)} />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="merchant-identifier">{t('domains.merchantIdentifier')}</Label>
                <Input
                  id="merchant-identifier"
                  value={merchantIdentifier}
                  onChange={(e) => setMerchantIdentifier(e.target.value)}
                  className="font-mono text-sm"
                />
                <p className="text-muted-foreground text-xs">{t('domains.merchantIdentifierHint')}</p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="domain-bt-registration">{t('domains.btRegistration')}</Label>
                <Input
                  id="domain-bt-registration"
                  value={btRegistrationId}
                  onChange={(e) => setBtRegistrationId(e.target.value)}
                  placeholder="00000000-0000-0000-0000-000000000000"
                  className="font-mono text-sm"
                />
                <p className="text-muted-foreground text-xs">{t('domains.btRegistrationHint')}</p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="merchant-name">{t('domains.merchantName')}</Label>
                  <Input
                    id="merchant-name"
                    value={merchantName}
                    onChange={(e) => setMerchantName(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="display-name">{t('domains.displayName')}</Label>
                  <Input
                    id="display-name"
                    value={displayName}
                    maxLength={64}
                    onChange={(e) => setDisplayName(e.target.value)}
                  />
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            {t('domains.cancel')}
          </Button>
          <Button disabled={!domain.trim() || addDomain.isPending} onClick={submit}>
            {t('domains.add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
